import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { PaginationFilterState } from "../../Interfaces/CustomDataGrid.interfaces";
import { safeBase64Encode, safeBase64Decode } from "../../../Utils/js-common";

const TABLE_STATE_URL_KEY = "tcf";

export const useTableStateUrl = (enableUrlParams: boolean = true) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getTableStateFromUrl = useCallback((): PaginationFilterState | null => {
    if (!enableUrlParams) return null;

    const tableStateParam = searchParams.get(TABLE_STATE_URL_KEY);
    if (!tableStateParam) return null;

    try {
      const decodedState = safeBase64Decode(tableStateParam);
      return JSON.parse(decodedState);
    } catch (error) {
      console.warn("Failed to parse URL table state parameters:", error);
      return null;
    }
  }, [searchParams, enableUrlParams]);

  const updateTableStateInUrl = useCallback(
    (updatedState: Partial<PaginationFilterState>, isUpdatingFromUrl: boolean = false) => {
      if (!enableUrlParams || isUpdatingFromUrl) return;

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        const currentState = getTableStateFromUrl() || {};
        const mergedState = { ...currentState, ...updatedState };

        // Clean up empty properties
        Object.keys(mergedState).forEach((key) => {
          const value = mergedState[key as keyof PaginationFilterState];
          if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) {
            delete mergedState[key as keyof PaginationFilterState];
          }
        });

        if (Object.keys(mergedState).length > 0) {
          try {
            const jsonString = JSON.stringify(mergedState);
            const encodedValue = safeBase64Encode(jsonString);
            newParams.set(TABLE_STATE_URL_KEY, encodedValue);
          } catch (error) {
            console.warn("Failed to encode table state to URL:", error);
            // Optionally, you could fall back to not setting the parameter
          }
        } else {
          newParams.delete(TABLE_STATE_URL_KEY);
        }

        return newParams;
      });
    },
    [enableUrlParams, getTableStateFromUrl, setSearchParams]
  );

  const clearTableStateFromUrl = useCallback(() => {
    if (!enableUrlParams) return;

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete(TABLE_STATE_URL_KEY);
      return newParams;
    });
  }, [enableUrlParams, setSearchParams]);

  return {
    getTableStateFromUrl,
    updateTableStateInUrl,
    clearTableStateFromUrl
  };
};
